import { Calendar, CursorClick, EnvelopeSimple, Image, ListChecks, TextT } from '@phosphor-icons/react';
import { Box, Card, CardContent, Chip, Container, Stack, Typography } from '@mui/material';
import { SectionEyebrow, interactiveCardSx } from '@scoutingops/ui';

const blocks = [
  {
    icon: Image,
    title: 'Hero Banner',
    description: 'A bold header with your unit name, number, and a photo. Sets the tone instantly.',
    tag: 'Pre-filled',
  },
  {
    icon: TextT,
    title: 'Text Sections',
    description: 'About your unit, leadership info, meeting details \u2014 just type and publish.',
    tag: 'Pre-filled',
  },
  {
    icon: Calendar,
    title: 'Event Calendar',
    description: "Campouts, meetings, service projects, badge events. Families always know what\u2019s next.",
    tag: 'Drag and drop',
  },
  {
    icon: EnvelopeSimple,
    title: 'Contact Form',
    description: 'Safe contact forms instead of exposed email addresses. Built around youth program communication guidelines.',
    tag: 'Youth-safe',
  },
  {
    icon: CursorClick,
    title: 'Join / CTA Buttons',
    description: 'Link to your program\u2019s registration, council page, or any URL. One click to join.',
    tag: 'Customizable',
  },
  {
    icon: ListChecks,
    title: 'Lists and Resources',
    description: 'Supply lists, what to bring, advancement info, resources \u2014 organized and accessible.',
    tag: 'Flexible',
  },
];

const pages = ['Home', 'About Us', 'Calendar', 'Join', 'Contact'];

export default function WhatYouGetSection() {
  return (
    <Box component="section" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="lg">
        <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center', mb: 7 }}>
          <SectionEyebrow>The Solution</SectionEyebrow>
          <Typography variant="h3" sx={{ fontWeight: 800, maxWidth: 780 }}>
            A site builder that already knows what your unit needs
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            You don&apos;t start from a blank page. Pick your unit type and Scouting Ops Site pre-fills your site
            with program-specific content, pages, and structure. You just customize it.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', mb: 5.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>Pages included:</Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            {pages.map((page) => (
              <Chip key={page} label={page} variant="outlined" />
            ))}
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(3,minmax(0,1fr))' },
          }}
        >
          {blocks.map((block) => (
            <Card key={block.title} variant="outlined" sx={[{ height: '100%' }, interactiveCardSx]}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box
                    className="brand-cardIcon"
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'action.hover',
                      color: 'primary.main',
                    }}
                  >
                    <block.icon size={18} />
                  </Box>
                  <Chip size="small" label={block.tag} color="secondary" />
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{block.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{block.description}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
